import type { AuditionBasicsState } from './BrandAuditionBasicsStep';
import type { AuditionRole } from './BrandAuditionRolesStep';
import type { ILocation } from '../../types/global/helper';

/** Values accepted by backend audition schema for `submission_report`. */
const SUBMISSION_REPORT_ALLOWED = new Set([
    'daily digest',
    'weekly digest',
    'monthly digest',
    'applications as they come in',
]);

/** Values accepted by backend for `union_affiliation` (per audition schema). */
const UNION_AFFILIATION_ALLOWED = new Set([
    'AEA',
    'SAG-AFTRA',
    'non-union',
    'not applicable',
    'ACTRA',
    'CAEA',
    'Equity',
    'MEAA',
    'other',
    'UBCP/ACTRA',
    'UDA',
    'AGN',
    'SAGA',
    'CWUSA',
    'NANTAP',
    'KAG',
]);

function normalizeSubmissionReport(raw: string): string {
    const v = (raw || 'daily digest').trim().toLowerCase();
    if (v === 'prerequisite') return 'daily digest';
    if (SUBMISSION_REPORT_ALLOWED.has(v)) return v;
    return 'daily digest';
}

function normalizeUnionAffiliation(raw: string): string {
    const v = (raw || 'non-union').trim();
    if (UNION_AFFILIATION_ALLOWED.has(v)) return v;
    const lower = v.toLowerCase();
    for (const allowed of UNION_AFFILIATION_ALLOWED) {
        if (allowed.toLowerCase() === lower) return allowed;
    }
    if (lower === 'non-union affiliation') return 'other';
    return 'non-union';
}

function labelToLocation(label: string): ILocation {
    const t = (label || '').trim();
    const short = t.split(',')[0]?.trim() || t;
    return {
        type: 'Point',
        coordinates: [0, 0],
        address: t,
        description: t,
        short_name: short,
    };
}

function parseAgeInt(s: string | undefined, fallback: number): number {
    const n = parseInt(String(s ?? '').trim(), 10);
    return Number.isFinite(n) ? n : fallback;
}

function normalizeEmploymentType(raw: string): string {
    const k = (raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const map: Record<string, string> = {
        contract: 'contract',
        'full time': 'full time',
        fulltime: 'full time',
        'one-off': 'one-off',
        'one off': 'one-off',
        charity: 'charity',
        ongoing: 'ongoing',
    };
    return map[k] || 'one-off';
}

const PORTFOLIO_TO_REQUIRED_MEDIA: Record<string, string> = {
    headshot: 'head shot',
    'head shot': 'head shot',
    'past work': 'portfolio',
    portfolio: 'portfolio',
    'audio reel': 'audio reel',
    'video reel': 'demo reel',
    'full body picture': 'fullbody shot',
};

function mapPortfolioToRequiredMedia(labels: string[]): string[] {
    const out: string[] = [];
    for (const raw of labels || []) {
        const key = raw.trim().toLowerCase();
        const mapped = PORTFOLIO_TO_REQUIRED_MEDIA[key];
        if (mapped && !out.includes(mapped)) out.push(mapped);
    }
    return out;
}

function mapBrandRoleToApiBody(basics: AuditionBasicsState, r: AuditionRole): Record<string, unknown> {
    const basicsMin = parseAgeInt(basics.min_age, 1);
    const basicsMax = Math.max(basicsMin, parseAgeInt(basics.max_age, 100));
    const gigRaw = (r.gig_type || basics.gig_type || 'cast').trim().toLowerCase();
    const roleGig = gigRaw === 'crew' || gigRaw === 'technical' || gigRaw === 'cast' ? gigRaw : 'cast';

    const isCast = roleGig === 'cast';
    let minAge: number;
    let maxAge: number;
    if (isCast) {
        minAge = parseAgeInt(r.min_age, basicsMin);
        maxAge = Math.max(minAge, parseAgeInt(r.max_age, Math.max(minAge, basicsMax)));
    } else {
        minAge = basicsMin;
        maxAge = basicsMax;
    }

    const expRaw = (r.gig_expertise || 'beginner').trim().toLowerCase();
    const expertise = isCast
        ? 'mid-level'
        : expRaw === 'expert'
        ? 'expert'
        : expRaw === 'mid-level' || expRaw === 'mid level'
        ? 'mid-level'
        : 'beginner';

    const compType = (r.compensation_type || '').trim().toLowerCase();
    const is_paid: 'paid' | 'unpaid' | 'tbd' =
        compType === 'unpaid' ? 'unpaid' : compType === 'tbd' ? 'tbd' : Number(r.compensation) > 0 ? 'paid' : 'unpaid';

    const required_media = mapPortfolioToRequiredMedia(r.portfolio_requirement || []);

    return {
        character: r.character.trim(),
        description: (r.description || '').trim(),
        gender: 'any',
        min_age: minAge,
        max_age: maxAge,
        age_range: `${minAge}-${maxAge}`,
        compensation: Number(r.compensation) || 0,
        currency: (r.currency || 'USD').toUpperCase(),
        expertise,
        role_type: roleGig,
        is_paid,
        employment_type: normalizeEmploymentType(r.employment_type),
        required_media,
        requires_subscription: false,
        nudity_or_kissing: false,
    };
}

export type BrandCreateAuditionDispatchPayload = Record<string, unknown> & {
    publishStatus: 'publish' | 'draft' | 'schedule';
    imageCover: File[] | string;
    production_company?: string;
};

/**
 * Shape the brand wizard state into the flat object consumed by `createAudition` / `parseAuditionPayload`,
 * aligned with backend audition + role fields (no client-only keys on the audition POST body).
 */
export function buildBrandAuditionCreatePayload(
    basics: AuditionBasicsState,
    roles: AuditionRole[],
): BrandCreateAuditionDispatchPayload {
    const loc = labelToLocation(basics.shoot_location);
    const anyPaid = roles.some(r => {
        const t = (r.compensation_type || '').toLowerCase();
        return t === 'paid' && Number(r.compensation) > 0;
    });
    const auditionCompensation: 'paid' | 'unpaid' | 'tbd' = anyPaid ? 'paid' : 'unpaid';
    const totalReward = roles.reduce((sum, r) => sum + Number(r.compensation || 0), 0);

    const firstEmp = roles.length ? normalizeEmploymentType(roles[0].employment_type) : 'one-off';

    const status: 'draft' | 'published' =
        basics.publishStatus === 'draft' || basics.publishStatus === 'schedule' ? 'draft' : 'published';

    const audition_type = basics.audition_type.trim() ? [basics.audition_type.trim().toLowerCase()] : [];

    const attachmentFile = basics.attachment_raw instanceof File ? basics.attachment_raw : null;
    const attachmentField: File | string = attachmentFile ?? (basics.attachment?.trim() || '');

    const imageCover: File[] | string =
        Array.isArray(basics.imageCover) && basics.imageCover.length > 0
            ? basics.imageCover
            : basics.imageCover === 'no-image'
            ? 'no-image'
            : basics.imageCover || 'no-image';

    const payload: BrandCreateAuditionDispatchPayload = {
        publishStatus: basics.publishStatus || 'publish',
        imageCover,
        title: basics.title.trim().slice(0, 200),
        synopsis: basics.synopsis.trim(),
        plot: (basics.plot || '').trim(),
        tags: Array.isArray(basics.tags) ? basics.tags.map(t => String(t).trim()).filter(Boolean) : [],
        audition_type,
        gig_type: (basics.gig_type || 'cast').trim().toLowerCase(),
        type_of_work: basics.type_of_work.trim(),
        union_affiliation: normalizeUnionAffiliation(basics.union_affiliation),
        submission_report: normalizeSubmissionReport(basics.submission_report),
        min_age: parseAgeInt(basics.min_age, 1),
        max_age: Math.max(parseAgeInt(basics.min_age, 1), parseAgeInt(basics.max_age, 100)),
        required_skill: Array.isArray(basics.required_skills)
            ? basics.required_skills.map(s => String(s).trim()).filter(Boolean)
            : [],
        required_media: [] as string[],
        shoot_location: loc,
        work_location: loc,
        talent_location: loc,
        location: loc,
        casting_expiration: basics.casting_expiration.trim(),
        job_start_date: basics.job_start_date.trim(),
        compensation: auditionCompensation,
        employment_type: firstEmp,
        reward: totalReward,
        min_reward: 0,
        max_reward: totalReward,
        reward_frequency: 'gig',
        currency: (roles[0]?.currency || 'USD').toUpperCase(),
        status,
        ...(basics.production_company?._id ? { production_company: basics.production_company._id } : {}),
        role: roles.map(r => mapBrandRoleToApiBody(basics, r)),
        attachment: attachmentField,
        is_background_color: Boolean(basics.is_background_color),
        background_color: basics.background_color || '',
        company_website: '',
        production_personel: '',
        usage: [] as string[],
        timezone: 'America/New_York',
    };

    const sched = basics.scheduled_published_date?.trim();
    if (sched) {
        (payload as Record<string, unknown>).scheduled_published_date = sched;
    }

    return payload;
}
